import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { cn } from "@/utils";
import { debounce } from "lodash";
import apiService from "@/services/api";

/** Данные адреса из DaData */
export interface AddressData {
  value: string;
  region: string | null;
  region_type_full: string | null;
  area: string | null;
  city: string | null;
  settlement: string | null;
  /** Кадастровый номер земельного участка */
  stead_cadnum: string | null;
  /** Кадастровый номер дома */
  house_cadnum: string | null;
  /** Кадастровый номер квартиры */
  flat_cadnum: string | null;
  /** Площадь квартиры */
  flat_area: string | null;
  /** Рыночная стоимость м² */
  square_meter_price: string | null;
  /** Рыночная стоимость квартиры */
  flat_price: string | null;
}

interface DadataSuggestion {
  value: string;
  unrestricted_value: string;
  data: {
    city: string | null;
    city_type_full: string | null;
    settlement: string | null;
    settlement_type_full: string | null;
    region: string | null;
    region_type_full: string | null;
    area: string | null;
    area_type_full: string | null;
    country: string | null;
    /** Код ФИАС */
    fias_id: string | null;
    /** Код КЛАДР */
    kladr_id: string | null;
    /** Улица */
    street: string | null;
    /** Дом */
    house: string | null;
    /** Квартира */
    flat: string | null;
    /** Кадастровый номер земельного участка */
    stead_cadnum: string | null;
    /** Кадастровый номер дома */
    house_cadnum: string | null;
    /** Кадастровый номер квартиры */
    flat_cadnum: string | null;
    /** Площадь квартиры */
    flat_area: string | null;
    /** Рыночная стоимость м² */
    square_meter_price: string | null;
    /** Рыночная стоимость квартиры */
    flat_price: string | null;
  };
}

interface AddressSuggestProps {
  value: string;
  onChange: (value: string) => void;
  /** Callback при выборе адреса с полными данными */
  onSelect?: (data: AddressData) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Ограничить до населенного пункта (без улиц и домов) */
  toSettlement?: boolean;
}

const DADATA_API_URL = "https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address";
const DADATA_TOKEN = import.meta.env.VITE_DADATA_API_KEY || "";

const AddressSuggest = forwardRef<HTMLInputElement, AddressSuggestProps>(
  (
    {
      value,
      onChange,
      onSelect,
      onBlur,
      placeholder = "Начните вводить...",
      className,
      disabled = false,
      toSettlement = true,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(value || "");
    const [suggestions, setSuggestions] = useState<DadataSuggestion[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Expose the input element via ref
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Синхронизация с внешним value
    useEffect(() => {
      setInputValue(value || "");
    }, [value]);

    // Закрытие dropdown при клике вне
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchSuggestions = async (query: string) => {
      if (!query || query.length < 2 || !DADATA_TOKEN) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const body: Record<string, unknown> = {
          query,
          count: 7,
        };

        // Ограничиваем до уровня населенного пункта
        if (toSettlement) {
          body.from_bound = { value: "region" };
          body.to_bound = { value: "settlement" };
        }

        const response = await fetch(DADATA_API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Token ${DADATA_TOKEN}`,
          },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
          setIsOpen(data.suggestions?.length > 0);
        }
      } catch (error) {
        console.error("DaData API error:", error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetch = useCallback(
      debounce((query: string) => fetchSuggestions(query), 300),
      [toSettlement],
    );

    useEffect(() => {
      return () => {
        debouncedFetch.cancel();
      };
    }, [debouncedFetch]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      onChange(newValue);
      debouncedFetch(newValue);
    };

    const handleSelect = (suggestion: DadataSuggestion) => {
      const selectedValue = suggestion.value;
      setInputValue(selectedValue);
      onChange(selectedValue);
      setIsOpen(false);
      setSuggestions([]);

      // Передаём полные данные адреса синхронно (для немедленного сворачивания)
      if (onSelect) {
        // Сначала используем данные из DaData для региона
        let fullRegion: string | null = null;
        if (suggestion.data.region) {
          fullRegion = suggestion.data.region;
          if (suggestion.data.region_type_full) {
            fullRegion = `${suggestion.data.region} ${suggestion.data.region_type_full}`;
          }
        }

        // Вызываем onSelect сразу с данными DaData
        onSelect({
          value: selectedValue,
          region: fullRegion,
          region_type_full: suggestion.data.region_type_full,
          area: suggestion.data.area,
          city: suggestion.data.city,
          settlement: suggestion.data.settlement,
          stead_cadnum: suggestion.data.stead_cadnum,
          house_cadnum: suggestion.data.house_cadnum,
          flat_cadnum: suggestion.data.flat_cadnum,
          flat_area: suggestion.data.flat_area,
          square_meter_price: suggestion.data.square_meter_price,
          flat_price: suggestion.data.flat_price,
        });

        // Асинхронно обогащаем регион через Pullenti (опционально)
        apiService
          .validateAddress({ address: selectedValue })
          .then((response) => {
            const addressDE = response?.data?.address;
            if (addressDE?.region) {
              let pullentiRegion = addressDE.region.value;
              if (addressDE.region.full_name) {
                pullentiRegion = `${addressDE.region.value} ${addressDE.region.full_name}`;
              }
              // Обновляем регион через повторный вызов onSelect с обогащёнными данными
              onSelect({
                value: selectedValue,
                region: pullentiRegion,
                region_type_full: suggestion.data.region_type_full,
                area: suggestion.data.area,
                city: suggestion.data.city,
                settlement: suggestion.data.settlement,
                stead_cadnum: suggestion.data.stead_cadnum,
                house_cadnum: suggestion.data.house_cadnum,
                flat_cadnum: suggestion.data.flat_cadnum,
                flat_area: suggestion.data.flat_area,
                square_meter_price: suggestion.data.square_meter_price,
                flat_price: suggestion.data.flat_price,
              });
            }
          })
          .catch((error) => {
            console.error("Pullenti validation error:", error);
            // Регион уже установлен из DaData, ничего не делаем
          });
      }
    };

    const handleFocus = () => {
      if (suggestions.length > 0) {
        setIsOpen(true);
      } else if (inputValue.length >= 2) {
        debouncedFetch(inputValue);
      }
    };

    const handleBlur = () => {
      // Откладываем вызов onBlur чтобы клик по подсказке успел отработать
      setTimeout(() => {
        onBlur?.();
      }, 150);
    };

    const formatSuggestionSubtitle = (data: DadataSuggestion["data"]): string => {
      const parts: string[] = [];

      if (data.region && data.region !== data.city) {
        parts.push(data.region);
      }
      if (data.area) {
        parts.push(data.area);
      }

      return parts.join(", ");
    };

    return (
      <div className="relative w-full" ref={containerRef}>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              "block w-full px-3 py-2 border border-line rounded-lg bg-surface-elevated text-fs-sm text-ink-1 placeholder:text-ink-4",
              "transition-colors focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30",
              className,
            )}
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        <AnimatePresence>
          {isOpen && suggestions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute left-0 right-0 mt-1 bg-surface-elevated rounded-lg shadow-lg border border-line z-50 max-h-64 overflow-y-auto"
            >
              {suggestions.map((suggestion, index) => {
                const subtitle = formatSuggestionSubtitle(suggestion.data);
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(suggestion)}
                    className="w-full text-left px-3 py-2 hover:bg-surface-hover transition-colors flex items-start gap-2"
                  >
                    <MapPin size={16} className="text-ink-3 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-ink-1 truncate">{suggestion.value}</div>
                      {subtitle && <div className="text-xs text-ink-3 truncate">{subtitle}</div>}
                    </div>
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

AddressSuggest.displayName = "AddressSuggest";

export default AddressSuggest;
